import axios from '@/axios';
import { $settings } from '@/provider/settings';
import { useOverviewStore } from '@/store/overview';
import moduleObject from '@/store/object/moduleObject';
import { $store, $i18n } from '@/provider/plugin';

const DASHBOARD_SYSTEM = '00000000000000000000000000000000';

// TODO: BE should remove "GUID:" from the response
const extractGUID = (guidString) => {
  if (guidString.includes('GUID:')) return guidString.split(':')[1];
  return guidString || null;
};

export default {
  async fetchDashboardData(
    store,
    { type, subType, subTypes, options = {}, totals = false },
  ) {
    if (!subType && !subTypes) {
      console.warn('No subType or subTypes for dashboard call provided');
    }

    const records =
      subTypes && subTypes.length > 0 ? subTypes.join(',') : subType;

    // TODO: check if code duplicates can be extracted or otherwise avoided
    const { skip = 0, activeSort = {}, pageAll } = options;

    const getTotals = totals ? '&totals=X' : '';
    const skipParameter = `&skip=${skip || 0}`;
    const pageSizeParameter = pageAll ? '&pageSize=999999' : '';
    const activeSortParameter =
      (activeSort.sort &&
        `&sort=${activeSort.sort} ${activeSort.asc ? 'asc' : 'desc'}`) ||
      '';

    const response = await axios({
      method: 'get',
      url: `${$settings.middlewareSAPProxyURI}/${type}/dashboard/?Records=${records}${skipParameter}${activeSortParameter}${pageSizeParameter}${getTotals}`,
    });

    if (response && response.data && response.data.DATA) {
      const { DATA, INFO } = response.data;
      const mappedData = {};

      (subTypes || [subType]).forEach((indexSubType) => {
        mappedData[indexSubType] =
          indexSubType.search('GUID') === -1
            ? {
                value: DATA[indexSubType] || [],
                total: (DATA.TOTALS && DATA.TOTALS[indexSubType]) || 0,
              }
            : {
                value: DATA || [],
                total: INFO.TOTAL || 0,
              };

        useOverviewStore().setOverview({
          context: pageAll ? 'queryAll' : 'query',
          type,
          subType: indexSubType,
          payload: mappedData[indexSubType],
        });
      });
    }

    return [];
  },
  async fetchDashboardConfig(store, { type, subType, subTypes }) {
    const records =
      subTypes && subTypes.length > 0 ? `?Records=${subTypes.join(',')}` : '';

    const response = await axios({
      method: 'get',
      url: `${$settings.middlewareSAPProxyURI}/${type}/dashboard/${records}`,
    });

    if (response && response.data && response.data.DATA) {
      const mappedData = {};

      (subTypes || [subType]).forEach((indexSubType) => {
        mappedData[indexSubType] = response.data.DATA[indexSubType] || [];

        useOverviewStore().setOverview({
          context: 'query',
          type,
          subType: indexSubType,
          payload: mappedData[indexSubType],
        });
      });
    }

    return [];
  },
  async updateDashboard(store, { payload, type }) {
    const response = await axios({
      method: 'post',
      data: { QUERYRESULT: payload },
      url: `${$settings.middlewareSAPProxyURI}/${type}/dashboard/`,
    });

    const { UPD_AREAS = [], SUCCESS } = response.data;

    if (!SUCCESS) return {};

    // TODO: BE should provide correct format
    const updateAreasMapped = UPD_AREAS.map(({ PARENT, BOTYPE, SUBTYPE }) => ({
      type: BOTYPE,
      subType: SUBTYPE,
      parent: PARENT,
    }));

    return updateAreasMapped;
  },
  initDashboard({ commit }, data) {
    commit('SET_DASHBOARD_CONFIG', data.content);
  },

  // Dashboard Customization

  async fetchDashboards({ commit }) {
    const dashboardLoading = { area: 'dashboardOverview', loading: true };
    await commit('SET_DASHBOARD_LOADING', dashboardLoading);

    return axios({
      method: 'get',
      url: `${window.settings.middlewareSAPProxyURI}/config/dashboard`,
    })
      .then((response) => {
        if (response && response.data && response.data.DATA) {
          const fallbackGuid = DASHBOARD_SYSTEM;
          const defaultDashboard = response.data.DATA.find(
            (dashboard) => dashboard.default,
          );

          const payload = response.data.DATA.map((item, index) => ({
            ...item,
            itemId: index,
            visibilityDescr: item.teamDescr,
            default: !!item.default,
            favorite: !!item.favorite,
          }));

          commit(
            'SET_DEFAULT_DASHBOARD',
            defaultDashboard?.guid || fallbackGuid,
          );

          useOverviewStore().setOverview({
            context: 'dashboardList',
            type: 'dashboard',
            payload,
          });
        }
      })
      .finally(async () => {
        await commit('SET_DASHBOARD_LOADING', {
          ...dashboardLoading,
          loading: false,
        });
      });
  },

  async addDashboard({ dispatch }, payload) {
    try {
      const response = await axios({
        method: 'post',
        data: payload,
        url: `${$settings.middlewareSAPProxyURI}/config/dashboard`,
      });

      if (response && response.data && response.data.DATA)
        return extractGUID(response.data.DATA);

      return null;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      dispatch('fetchDashboards');
    }
  },
  async saveSelectedDashboard({ commit, dispatch }, { dashboard, guid }) {
    await axios({
      method: 'put',
      data: dashboard,
      url: `${$settings.middlewareSAPProxyURI}/config/dashboard/${guid}`,
    });
    commit('SAVE_SELECTED_DASHBOARD', dashboard);
    dispatch('fetchDashboards');
  },
  async deleteDashboard({ dispatch, state, commit }, guid) {
    await axios({
      method: 'delete',
      url: `${$settings.middlewareSAPProxyURI}/config/dashboard/${guid}`,
    });

    const { selectedDashboard = {} } = state;
    if (selectedDashboard.guid === guid) commit('SET_SELECTED_DASHBOARD', null);

    dispatch('fetchDashboards');
  },
  async loadDashboard({ commit }, guid) {
    if (guid === DASHBOARD_SYSTEM)
      return { guid: DASHBOARD_SYSTEM, name: 'System', system: true }; // TODO: Need Alias for System

    await commit('SET_DASHBOARD_LOADING', {
      area: 'dashboardDetail',
      loading: true,
    });

    const payload = await axios({
      method: 'get',
      url: `${$settings.middlewareSAPProxyURI}/config/dashboard/${guid}`,
    });

    await commit('SET_DASHBOARD_LOADING', {
      area: 'dashboardDetail',
      loading: false,
    });

    return payload && payload.data && payload.data.DATA;
  },
  async setSelectedDashboard({ commit, dispatch }, dashboard) {
    const { guid, owner } = dashboard;
    const dashboardData = await dispatch('loadDashboard', guid);

    if (dashboardData)
      commit('SET_SELECTED_DASHBOARD', { ...dashboardData, guid, owner });
  },
  resetSelectedDashboard({ commit }) {
    commit('SET_SELECTED_DASHBOARD', null);
  },
  async getWebsiteItems({ commit }) {
    const response = await axios({
      method: 'post',
      url: `${$settings.middlewareSAPProxyURI}/dashboard/autosuggest`,
      data: {
        SOURCE: {
          RECORD: 'dashboard',
          FIELD: 'weblink',
        },
      },
    });

    if (response && response.data) {
      commit('SET_DASHBOARD_ITEMS', {
        items: response.data.map(({ LINK, TEXT_ALIAS, ICON, COLOR }) => {
          const type = `website-${ICON}`;
          try {
            if (!$store.hasModule(type)) {
              $store.registerModule(type, moduleObject);
            }
            commit(
              `${type}/SET_SETTINGS`,
              {
                color: COLOR,
                icon: ICON,
              },
              { root: true },
            );
          } catch (error) {
            console.warn(error);
          }

          return {
            text: $i18n.global.t(TEXT_ALIAS),
            value: LINK,
            icon: ICON,
            color: COLOR,
            type,
          };
        }),
        type: 'website',
      });
    }
  },
  async getSmartKpisItems({ commit }) {
    const response = await axios({
      method: 'post',
      url: `${$settings.middlewareSAPProxyURI}/dashboard/autosuggest`,
      data: {
        SOURCE: {
          RECORD: 'dashboard',
          FIELD: 'block',
        },
      },
    });

    if (response && response.data) {
      commit('SET_DASHBOARD_ITEMS', {
        items: response.data.map(({ ID, TITLE, TYPE }) => ({
          text: TITLE,
          value: ID,
          type: TYPE,
        })),
        type: 'smart_kpis',
      });
    }
  },
  async getDashboardBOItems({ commit }, type) {
    const response = await axios({
      method: 'get',
      url: `${$settings.middlewareSAPProxyURI}/config/tvalue/dashboard/${type}`,
    });

    if (response && response.data && response.data.DATA) {
      commit('SET_DASHBOARD_ITEMS', { items: response.data.DATA, type });
    }
  },
  async getChartsItems({ commit }) {
    const response = await axios({
      method: 'post',
      url: `${$settings.middlewareSAPProxyURI}/dashboard/autosuggest`,
      data: {
        SOURCE: {
          RECORD: 'dashboard',
          FIELD: 'chart',
        },
      },
    });

    if (response && response.data) {
      commit('SET_DASHBOARD_ITEMS', {
        items: response.data.map(({ ID, TITLE, TYPE, CHART_TYPE }) => ({
          text: TITLE,
          value: ID,
          type: TYPE,
          chartType: CHART_TYPE,
        })),
        type: 'charts',
      });
    }
  },
};
